import {useState, useEffect} from "react";
import {
  Tile,
  CopyText,
  Heading,
  Button,
  Notification,
} from "@dtpk-cc/components";
import qs from "qs";
import PulseLoader from "react-spinners/PulseLoader";
import "@dtpk-cc/components/dist/global.css";
import "@dtpk-cc/components/dist/components/Tile/Tile.css";
import "@dtpk-cc/components/dist/components/CopyText/CopyText.css";
import "@dtpk-cc/components/dist/components/Heading/Heading.css";
import "@dtpk-cc/components/dist/components/Button/Button.css";
import "@dtpk-cc/components/dist/components/Notification/Notification.css";
import "@dtpk-cc/components/dist/components/Icon/Icon.css";
import "@dtpk-cc/components/dist/components/A11yText/A11yText.css";

import Autocomplete from "../Autocomplete";

import styles from './AvailabilityCheck.module.css';

function AvailabilityCheck({
  onAvailabilityCheckSuccess,
  onAvailabilityCheckReset,
}){

  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [houseNumberAddition, setHouseNumberAddition] = useState("");

  const [captcha, setCaptcha] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");

  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);

  const [acChecked, setAcChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const newCaptcha = (e) => {
    e?.preventDefault();
    newCaptchaRequest();
    setCaptcha("");
    if (errors.some((el) => el.field === "captchacode")) {
      document.getElementById("captcha").focus();
    }
  };

  const newCaptchaRequest = () => {
    return fetch(`${process.env.REACT_APP_AC_URL}/usesCaptcha.do?source=${process.env.REACT_APP_AC_SOURCE}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        data &&
          setCaptchaCode(
            `${process.env.REACT_APP_AC_URL}/captcha.jpg?znummer=${Math.floor(
              Math.random() * 1001
            )}`
          );
      });
  }
  
  const tariffsFound = () => {
    return ( response
          && response.empfehlungen
          && response.empfehlungen.empfehlung
          && !response.empfehlungen.empfehlung.some((tariff) => tariff.kenner === 'idb'))
  };

  useEffect(() => {
    newCaptcha();
  }, []);

  const handleCaptchaChange = (e) => {
    setCaptcha(e.target.value);
  };

  const handleSubmit = async (e) => {
    setErrors([]);
    e.preventDefault();
    if (!city) {
      setErrors([{ field: "ort" }]);
      return;
    } else if (!street) {
      setErrors([{ field: "strasse" }]);
      return;
    } else if (!houseNumber) {
      setErrors([{ field: "hausnummer" }]);
      return;
    }
    setLoading(true);
    const address = {
      plz: zip,
      ort: city,
      strasse: street,
      ortsteil: district,
      hausnummer: houseNumber,
      hausnummerzusatz: houseNumberAddition,
      geschaeftsfall: '2000',
    };
    const params = {
      source: process.env.REACT_APP_AC_SOURCE,
      empfehlung: true,
      captchacode: captcha,
    };

    const data = { ...address, ...params };

    await newCaptchaRequest();

    fetch(`${process.env.REACT_APP_AC_URL}/ace.do`, {
      method: "POST",
      body: qs.stringify(data),
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.validierungsFehler.length > 0) {
          setErrors(response.validierungsFehler);
          setLoading(false);
          setSuccess(false);
        } else if (response.unbekannterFehler) {
          setAcChecked(true);
          setLoading(false);
          setSuccess(false);
        } else {
          setResponse(response);
          onAvailabilityCheckSuccess(response);
          setSuccess(true);
          setAcChecked(true);
          setLoading(false);
        }
        newCaptcha();
      })
      .catch(() => {
        setAcChecked(true);
        setLoading(false);
        setSuccess(false);
      });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setAcChecked(false);
    setLoading(false);
    setCaptcha("");
    newCaptcha();
    onAvailabilityCheckReset();
  };

  const getMaxMbit = (speed, max) => {
    let speedMbit = speed / 1000;

    if( speedMbit > max ){
      return max;
    }

    return speedMbit;
  };

  return (
    <>
      <div className={styles.notifications}>
        {/* Nicht erfolgreicher Check */}
        {acChecked && !success && <Notification
          headline="Es tut uns leid. Zur Zeit ist eine Verfügbarkeitsprüfung leider nicht möglich. Bitte versuchen Sie es später erneut."
          variant={Notification.Variant.alert}
        >
          <Notification.CopyText>
            Ihre geprüfte Adresse: {zip} {city}, {street} {houseNumber}{" "}
            {houseNumberAddition}
          </Notification.CopyText>
          <Notification.Link>
            <span onClick={handleReset}>Erneut versuchen</span>
          </Notification.Link>
        </Notification>}

        {/* Erfolgreicher Check und es wurden Tarife gefunden */}
        {acChecked && success && tariffsFound() && <Notification
          headline={`MagentaZuhause ist mit bis zu ${getMaxMbit(response.maxDownstream, 250)} MBit/s im Download und bis zu ${getMaxMbit(response.maxUpstream, 50)} MBit/s im Upload verfügbar.`}
          variant={Notification.Variant.confirmation}
        >
          <Notification.CopyText>
            Ihre geprüfte Adresse: {zip} {city}, {street} {houseNumber}{" "}
            {houseNumberAddition}
          </Notification.CopyText>
          <Notification.Link>
            <span onClick={handleReset}>Angaben ändern</span>
          </Notification.Link>
        </Notification>}

        {/* Erfolgreicher Check und es wurden KEINE Tarife gefunden */}
        {acChecked && success && !tariffsFound() && <Notification
          headline="An der Adresse ist leider kein MagentaZuhause Festnetz-Tarif verfügbar."
          variant={Notification.Variant.alert}
        >
          <Notification.CopyText>
            Ihre geprüfte Adresse: {zip} {city}, {street} {houseNumber}{" "}
            {houseNumberAddition}
          </Notification.CopyText>
          <Notification.Link>
            <span onClick={handleReset}>Angaben ändern</span>
          </Notification.Link>
        </Notification>}
      </div>

        {/* Check wurde noch nicht durchgeführt */}
      {!acChecked && (
        <Tile variant="shadow" customClass={styles.container}>
          <Tile.Content>
            <Heading variants={[Heading.Variant.tertiary]}>
              Bitte prüfen Sie hier, mit welcher Internet-Geschwindigkeit <b>Magenta</b>Zuhause bei Ihnen verfügbar ist.
            </Heading>

            <form onSubmit={handleSubmit} noValidate>
              {/* fake form inputs to avoid autoComplete */}
              <div
                style={{
                  height: "0px",
                  visibility: "hidden",
                  position: "absolute",
                  opacity: 0,
                }}
              >
                <input name="_email" type="email" />
                <input name="_password" type="password" />
              </div>
              {/* end of fake form inputs to avoid autoComplete */}

              <Autocomplete
                zip={zip}
                city={city}
                street={street}
                errors={errors}
                onZipChange={(value) => setZip(value)}
                onCityChange={(value) => setCity(value)}
                onDistrictChange={(value) => setDistrict(value)}
                onStreetChange={(value) => setStreet(value)}
                onHouseNumberChange={(value) => setHouseNumber(value)}
                onHouseNumberAdditionChange={(value) => setHouseNumberAddition(value)}
              />
              <div className={styles['autocomplete-footer']}>
                <CopyText variants={[CopyText.Variant.additional]}>* Pflichtangaben</CopyText>
              </div>

              {captchaCode && (
                <>
                  <div className="captcha-container">
                    <div>
                      <img
                        width="216"
                        height="34"
                        alt="Captcha Grafik"
                        src={captchaCode}
                        onClick={newCaptcha}
                        className="captcha-container__img"
                      />
                      <a
                        href="#sicherheitscode"
                        onClick={newCaptcha}
                        className="captcha-link"
                      >
                        neuen Sicherheitscode generieren
                      </a>
                    </div>
                    <div className="captcha-container__input">
                      <div className="Input__wrapper___jV2Jt">
                        <input
                          id="captcha"
                          name="captcha"
                          type="text"
                          className={`Input__input___5vxBY ${errors.some((el) => el.field === "captchacode") ? 'is-invalid' : '' }`}
                          aria-invalid={!errors.some((el) => el.field === "captchacode")}
                          aria-describedby="captcha-msg"
                          value={captcha}
                          onChange={handleCaptchaChange}
                        />
                        <label htmlFor="captcha" className="Input__label___1zkjm">
                          Captcha*
                        </label>
                        {errors.some((el) => el.field === "captchacode") &&
                          <p id="captcha-msg" className="Input__message___3l57-">
                            Bitte geben Sie einen gültigen Captcha an.
                          </p>
                        }
                      </div>
                    </div>
                  </div>
                </>
              )}        

              <div className={styles.footer}>
                {loading ? (
                  <PulseLoader color="#e20074" />
                ) : (
                  <Button type="submit">Verfügbarkeit prüfen</Button>
                )}
              </div>
            </form>
          </Tile.Content>
        </Tile>
      )}
    </>
  )

}

AvailabilityCheck.defaultProps = {
  onAvailabilityCheckSuccess: () => {},
  onAvailabilityCheckReset: () => {},
}


export default AvailabilityCheck;